import React, { Dispatch, SetStateAction, useEffect } from "react";
import type { Control, UseFormHandleSubmit } from "react-hook-form";
import type { GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
  CardMedia,
  Paper,
  Modal,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Controller } from "react-hook-form";
import { PosTick, RicRipe, TabRuoli } from "../AppointmentResearch";
import { Enti } from "../../AppointmentManagement/AppointmentManagement";

type DettaglioFormValues = any;

type AppointmentDetailPanelProps = {
  onAskRestoreAppointment: (id: string) => void;
  // Per gestire i Tabs
  selectedTab: number;
  onTabChange: (event: React.SyntheticEvent, value: number) => void;

  //Per filtrare gli appuntamenti in base alla prestazione selezionata
  selectedPrestazioneId?: string | null;
  setSelectedAppointmentIDPACAPR?: Dispatch<SetStateAction<string | null>>;

  // (form Dettaglio)
  control: Control<DettaglioFormValues>;
  handleSubmit: UseFormHandleSubmit<DettaglioFormValues>;
  onSubmit: (data: DettaglioFormValues) => void;

  // Edit Prestazione (modale)
  editOpen: boolean;
  onEditClose: () => void;
  onEditAskConfirm: () => void;
  editControl: Control<any>;
  tabRuoli: TabRuoli[] | undefined;
  ricRipe: RicRipe[] | undefined;
  entiList: Enti[];
  posTicket: PosTick[] | undefined;
  specialitiesList: any[];
  filteredPerformances: any[];
  getPerfSpecialita: (p: any) => string;
  setEditValue: (...args: any[]) => void;
  pdfIconSrc?: string;

  // chiamato dalla tabella Prestazioni prenotate dal click della matitina nera
  onRequestEditPrestazione: (row: any) => void;

  // Dati delle tabelle
  prestazioniRows: any[];
  prestazioniColumns: GridColDef[];
  appuntamentiRows: any[];
  appuntamentiColumns: GridColDef[];
  onSelectPrestazione: (idPacapr: string) => void;

  // Funzioni per confermare/uscire
  onClickUscita: () => void;
  onAskConferma: () => void;

  // Storico Annullamenti
  StoricoAnnullamenti?: React.ComponentType<{
    appuntamentiAnnullati: any;
    handleOnClickDettaglioAppuntamento: (params: any) => void;
    handleOnClickRipristinoAppuntamento: (params: any) => void;
    handleOnClickUscita: () => void;
    askConfirmEditPrenotazione: () => void;
    askConfirmCancellazioneFisicaAppuntamentoCancellato: (params: any) => void;
    askConfirmRipristinoAppuntamento: (params: any) => void;
  }>;
  appuntamentiAnnullati: any[];
  onDettaglioAnnullato: (id: string) => void;
  onRipristino: (id: string) => void;
  onCancellazioneFisica: (id: string) => void;

  // Opzionali (Attualmente li stiamo usando ma c'è da controllare se servono effettivamente)
  iconCardSrc?: string;
  radioRowSx?: object;
  maxPriorityLength?: number;
  localeText?: Record<string, any>;
};

const AppointmentDetailPanel: React.FC<AppointmentDetailPanelProps> = ({
  onAskRestoreAppointment,
  selectedTab,
  onTabChange,
  control,
  tabRuoli,
  ricRipe,
  entiList,
  posTicket,
  handleSubmit,
  onSubmit,
  prestazioniRows,
  prestazioniColumns,
  appuntamentiRows,
  appuntamentiColumns,
  onSelectPrestazione,
  onClickUscita,
  onAskConferma,
  StoricoAnnullamenti,
  appuntamentiAnnullati,
  onDettaglioAnnullato,
  onRipristino,
  onCancellazioneFisica,
  iconCardSrc,
  radioRowSx,
  maxPriorityLength = 400,
  localeText,
  editOpen,
  onEditClose,
  onEditAskConfirm,
  editControl,
  specialitiesList,
  filteredPerformances,
  getPerfSpecialita,
  setEditValue,
  pdfIconSrc,
  onRequestEditPrestazione,
  selectedPrestazioneId,
  setSelectedAppointmentIDPACAPR,
}) => {
  const [priorityText, setPriorityText] = React.useState("");

  const filteredAppuntamentiRows = React.useMemo(() => {
    if (!selectedPrestazioneId) return [];

    return (appuntamentiRows || []).filter(
      (r) => String(r.idPacaPre) === String(selectedPrestazioneId)
    );
  }, [appuntamentiRows, selectedPrestazioneId]);

  //UseEffect per selezionare automaticamente la prestazione se ce n'è una sola
  useEffect(() => {
    //Se è assente o ce ne sono più di una non faccio nulla
    if (!prestazioniRows || prestazioniRows.length !== 1) return;
    //Se c'è già una prestazione selezionata non faccio nulla
    if (selectedPrestazioneId) return;

    const onlyPrestazione = prestazioniRows[0];

    // usa la stessa chiave che passiamo nel click della riga:
    // onrowclick = {(row) => onSelectPrestazione(row.row.id)}
    onSelectPrestazione(String(onlyPrestazione.id));

    if (setSelectedAppointmentIDPACAPR) {
      setSelectedAppointmentIDPACAPR(String(onlyPrestazione.id) as any);
    }
  }, [
    prestazioniRows,
    selectedPrestazioneId,
    onSelectPrestazione,
    setSelectedAppointmentIDPACAPR,
  ]);

  return (
    <Box
      sx={{
        p: 2,
        overflow: "auto",
        bgcolor: "transparent",
      }}
    >
      {/* Modale per gestire la lettura/modifica di una singola prestazione */}
      <Modal
        open={editOpen}
        //Impedsco al Modale di chiudersi al click esterno o con il tasto esc
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onEditClose();
        }}
      >
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "947px",
            width: "92%",
            overflowY: "auto",
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 500,
              fontFamily: "Rubik",
              color: "rgba(0, 0, 0, 0.60)",
              fontSize: 14,
              fontStyle: "normal",
              lineHeight: "24px",
              letterSpacing: "-0.14px",
            }}
          >
            Prestazioni Prenotate
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="specialita"
                control={editControl}
                rules={{ required: "Questo campo è obbligatorio" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    size="small"
                    options={specialitiesList || []}
                    getOptionLabel={(o) => o.DESCRIZIONE || ""}
                    value={
                      (specialitiesList || []).find(
                        (o) => o.SPECIALITA === value
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      onChange(newValue ? newValue.SPECIALITA : "")
                    }
                    isOptionEqualToValue={(opt, val) =>
                      opt.SPECIALITA === val?.SPECIALITA
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.SPECIALITA}>
                        {option.DESCRIZIONE}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialità"
                        fullWidth
                        helperText={error?.message || null}
                      />
                    )}
                    filterOptions={(opts) => {
                      const seen = new Set<string>();
                      return opts.filter((o) =>
                        seen.has(o.SPECIALITA)
                          ? false
                          : (seen.add(o.SPECIALITA), true)
                      );
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="prestazione"
                control={editControl}
                rules={{ required: "Questo campo è obbligatorio" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    size="small"
                    options={filteredPerformances || []}
                    getOptionLabel={(o) => o.DESPRESTAZIONE || ""}
                    value={
                      (filteredPerformances || []).find(
                        (o) => o.PRESTAZIONE === value
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const code = newValue ? newValue.PRESTAZIONE : "";
                      onChange(code); // setta il codice prestazione nel form
                      // auto-set della specialità associata
                      const spec = newValue ? getPerfSpecialita(newValue) : "";
                      if (spec) {
                        setEditValue("specialita", spec, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }}
                    isOptionEqualToValue={(opt, val) =>
                      opt.PRESTAZIONE === val?.PRESTAZIONE
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.PRESTAZIONE}>
                        {option.DESCRIZIONE}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Prestazione"
                        fullWidth
                        helperText={error?.message || null}
                      />
                    )}
                    filterOptions={(opts) => {
                      const seen = new Set<string>();
                      return opts.filter((o) =>
                        seen.has(o.PRESTAZIONE)
                          ? false
                          : (seen.add(o.PRESTAZIONE), true)
                      );
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="distretto"
                control={editControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Distretto"
                    size="small"
                    fullWidth
                    placeholder=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="importo"
                control={editControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Importo"
                    size="small"
                    fullWidth
                    placeholder=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="posizioneTicket"
                control={editControl}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={posTicket ?? []}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.CODPOSTIC ?? ""} - ${
                            option.DESPOSTIC ?? ""
                          }`
                        : ""
                    }
                    value={
                      posTicket?.find((r) => r.CODPOSTIC === field.value) ??
                      null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue?.CODPOSTIC ?? "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Posizione Ticket"
                        error={!!fieldState?.error}
                        helperText={fieldState?.error?.message}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.CODPOSTIC === value?.CODPOSTIC
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="ente"
                control={editControl}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={entiList ?? []}
                    getOptionLabel={(option) =>
                      option?.["ragione sociale"] ?? ""
                    }
                    value={
                      entiList?.find((r) => r.codice === field.value) ?? null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue?.codice ?? "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ente"
                        error={!!fieldState?.error}
                        helperText={fieldState?.error?.message}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.codice === value?.codice
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="ripetizione"
                control={editControl}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={ricRipe ?? []}
                    getOptionLabel={(option) => option?.DESRICRIPE ?? ""}
                    value={
                      ricRipe?.find((r) => r.CODRICRIPE === field.value) ?? null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue?.CODRICRIPE ?? "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ripetizione"
                        error={!!fieldState?.error}
                        helperText={fieldState?.error?.message}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.CODRICRIPE === value?.CODRICRIPE
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="idEventoConfirmo"
                control={editControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="ID Evento Confirmo"
                    size="small"
                    fullWidth
                    placeholder=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                sx={{
                  height: 40,
                  width: "100%",
                  "& .pdf-icon": { transition: "filter .2s" },
                  "&:hover .pdf-icon": {
                    filter: "brightness(0) invert(1)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={pdfIconSrc}
                  alt=""
                  className="pdf-icon"
                  sx={{ mr: 1, width: 20, height: 20 }}
                />
                Scarica PDF
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Controller
                name="temporaneo"
                control={editControl}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox {...field} checked={!!field.value} disabled />
                    }
                    label="Temporaneo"
                    sx={{ mr: 0 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container display="flex" justifyContent="space-between">
                <Grid item>
                  <Button variant="outlinedError" onClick={onEditClose}>
                    Uscita
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="containedCustom" onClick={onEditAskConfirm}>
                    Conferma
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Paper
        elevation={1}
        sx={{
          mx: "auto",
          p: 2,
          borderRadius: 2,
        }}
      >
        <Tabs value={selectedTab} onChange={onTabChange} sx={{ pl: 2, pb: 2 }}>
          <Tab
            sx={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              textTransform: "none",
            }}
            label="Dettaglio"
          />
          <Tab
            sx={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              textTransform: "none",
            }}
            label="Storico Annullamenti"
          />
        </Tabs>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {selectedTab === 0 && (
            <Grid container spacing={2}>
              {/* Riga 1 */}
              <Grid item xs={12} md={3}>
                <Controller
                  name="numeroPrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Num. Prenotazione"
                      disabled
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="dataPrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      type="date"
                      inputProps={{ max: "9999-12-31" }}
                      InputLabelProps={{ shrink: true }}
                      label="Dt. Prenotazione"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="idPrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField size="small" label="ID" fullWidth {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="totPrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Tot. Prest"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>

              {/* Riga 2 */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="paziente"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      disabled
                      label="Paziente"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="dataDiNascita"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      disabled
                      type="date"
                      inputProps={{ max: "9999-12-31" }}
                      InputLabelProps={{ shrink: true }}
                      label="Data di Nascita"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Controller
                  name="sesso"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      disabled
                      label="Sesso"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                    "&:hover .icon-img": { filter: "brightness(0) invert(1)" },
                  }}
                  variant="outlined"
                  onClick={() => console.log("Varia anagrafica")}
                >
                  {iconCardSrc && (
                    <CardMedia
                      component="img"
                      className="icon-img"
                      sx={{ mr: 1, height: 20, width: 20 }}
                      image={iconCardSrc}
                    />
                  )}
                  Varia Anagrafica
                </Button>
              </Grid>

              {/* Riga 3 */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="telefono"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Telefono"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="praticaRicovero"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Pratica Ricovero"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>

              {/* Origine */}
              <Grid item xs={12}>
                <Controller
                  name="origine"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={radioRowSx}>
                      <Grid item xs={12} pb={1}>
                        <Typography
                          variant="h6"
                          fontSize={14}
                          fontFamily="Rubik"
                          color="rgba(0,0,0,0.60)"
                          fontWeight={500}
                        >
                          Origine
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Grid item md={2}>
                          <FormControlLabel
                            value="P"
                            control={<Radio size="small" />}
                            label="Sys Clinique"
                            disabled
                          />
                        </Grid>
                        <Grid item md={1}>
                          <FormControlLabel
                            value="X"
                            control={<Radio size="small" />}
                            label="Gp++"
                            disabled
                          />
                        </Grid>
                        <Grid item md={1}>
                          <FormControlLabel
                            value="G"
                            control={<Radio size="small" />}
                            label="G2"
                            disabled
                          />
                        </Grid>
                        <Grid item md={2}>
                          <FormControlLabel
                            value="T"
                            control={<Radio size="small" />}
                            label="Tuo Tempo"
                            disabled
                          />
                        </Grid>
                        <Grid item md={1}>
                          <FormControlLabel
                            value="W"
                            control={<Radio size="small" />}
                            label="Web"
                            disabled
                          />
                        </Grid>
                        {/* <Grid item md={2}>
                          <FormControlLabel
                            value="MTA"
                            control={<Radio size="small" />}
                            label="Flusso MTA"
                            disabled
                          />
                        </Grid> */}
                        {/* <Grid item md={1}>
                          <FormControlLabel
                            value="ECLAIM"
                            control={<Radio size="small" />}
                            label="Eclaim"
                            disabled
                          />
                        </Grid> */}
                      </Grid>
                    </RadioGroup>
                  )}
                />
              </Grid>

              {/* Flag + altri campi */}
              <Grid item xs={12} md={2}>
                <Controller
                  name="temporaneo"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox {...field} checked={!!field.value} disabled />
                      }
                      label="Temporaneo"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Controller
                  name="ruoloOperatore"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={tabRuoli ?? []}
                      getOptionLabel={(option) => option?.DESTABRUOLI ?? ""}
                      value={
                        tabRuoli?.find((r) => r.CODTABRUOLI === field.value) ??
                        null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue?.CODTABRUOLI ?? "")
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ruolo Operatore"
                          error={!!fieldState?.error}
                          helperText={fieldState?.error?.message}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option?.CODTABRUOLI === value?.CODTABRUOLI
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Controller
                  name="aslPrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="ASL prenotazione"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>

              {/* Note */}
              <Grid item xs={12}>
                <Controller
                  name="notePrenotazione"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Note Prenotazione"
                      placeholder="Note prenotazione"
                      multiline
                      rows={4}
                      error={
                        field.value
                          ? field.value.length > maxPriorityLength
                          : false
                      }
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e);
                        setPriorityText(e.target.value);
                      }}
                      helperText={`(${
                        field.value ? field.value.length : 0
                      }/${maxPriorityLength})`}
                    />
                  )}
                />
              </Grid>

              {/* DataGrid Prestazioni */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  fontSize={14}
                  fontFamily="Rubik"
                  color="rgba(0,0,0,0.60)"
                  fontWeight={500}
                >
                  Prestazioni prenotate
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    height: 200,
                  }}
                  borderRadius={2}
                >
                  <DataGrid
                    sx={{
                      border: "1px solid #ccc",
                      width: "100%",
                      borderRadius: 2,
                      display: "flex",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#003267",
                        color: "#FFFFFF",
                      },
                      "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "#F8F9FC",
                      },
                      "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#FFFFFF",
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: "bold",
                      },
                      "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
                        display: "none",
                      },
                      "& .MuiDataGrid-iconButtonContainer": {
                        color: "#FFFFFF",
                      },
                      "& .MuiDataGrid-sortIcon": { color: "#FFFFFF" },
                      "& .MuiDataGrid-columnSeparator": { display: "none" },
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "#DFEAFF !important",
                      },
                    }}
                    rows={prestazioniRows}
                    columns={prestazioniColumns}
                    getRowId={(row) => row.id}
                    onRowClick={(row) => onSelectPrestazione(row.row.id)}
                    headerHeight={36}
                    rowHeight={36}
                    initialState={{
                      columns: { columnVisibilityModel: { id: false } },
                    }}
                    hideFooter
                    localeText={localeText}
                    disableColumnFilter
                    disableColumnMenu
                  />
                </Box>
              </Grid>

              {/* DataGrid Appuntamenti */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 500,
                    fontFamily: "Rubik",
                    color: "rgba(0,0,0,0.60)",
                    fontSize: 14,
                    lineHeight: "24px",
                    letterSpacing: "-0.14px",
                    m: 0,
                  }}
                >
                  Appuntamenti per prestazione
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    height: 200,
                  }}
                  borderRadius={2}
                >
                  <DataGrid
                    sx={{
                      border: "1px solid #ccc",
                      width: "100%",
                      borderRadius: 2,
                      display: "flex",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#003267",
                        color: "#FFFFFF",
                      },
                      "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "#F8F9FC",
                      },
                      "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#FFFFFF",
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: "bold",
                      },
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                    }}
                    rows={selectedPrestazioneId ? filteredAppuntamentiRows : []}
                    columns={appuntamentiColumns}
                    getRowId={(row) => Number(row?.id)}
                    headerHeight={36}
                    rowHeight={36}
                    initialState={{
                      columns: { columnVisibilityModel: { id: false } },
                    }}
                    hideFooter
                    localeText={localeText}
                    disableColumnFilter
                    disableColumnMenu
                  />
                </Box>
              </Grid>

              {/* Footer */}
              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Button
                      variant="outlinedError"
                      size="small"
                      onClick={onClickUscita}
                    >
                      Uscita
                    </Button>
                  </Grid>
                  <Grid item display="flex" gap={2}>
                    <Button
                      variant="outlinedCustom"
                      onClick={() => console.log("Acc. Manuale")}
                    >
                      Acc. Manuale
                    </Button>
                    <Button
                      variant="outlinedCustom"
                      onClick={() => console.log("Log GP")}
                    >
                      Log GP
                    </Button>
                    <Button
                      variant="containedCustom"
                      size="small"
                      onClick={() => {
                        onAskConferma();
                      }}
                    >
                      Conferma
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {selectedTab === 1 && StoricoAnnullamenti && (
            <Grid container spacing={2}>
              <StoricoAnnullamenti
                appuntamentiAnnullati={appuntamentiAnnullati}
                handleOnClickDettaglioAppuntamento={onDettaglioAnnullato}
                handleOnClickUscita={onClickUscita}
                handleOnClickRipristinoAppuntamento={onRipristino}
                askConfirmCancellazioneFisicaAppuntamentoCancellato={
                  onCancellazioneFisica
                }
                askConfirmRipristinoAppuntamento={onAskRestoreAppointment}
                askConfirmEditPrenotazione={onAskConferma}
              />
            </Grid>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AppointmentDetailPanel;
